/**
 * SEO Audit service — admin + public API client.
 */
import { get, put, post, del } from "../utils/api";

export async function fetchSeoAudit({ locales } = {}) {
  const q = new URLSearchParams();
  if (locales?.length) q.set("locales", locales.join(","));
  const qs = q.toString();
  const res = await get(`/admin/seo/audit${qs ? `?${qs}` : ""}`);
  return res?.data;
}

export async function fetchSeoAuditSingle(url) {
  const encoded =
    typeof Buffer !== "undefined"
      ? Buffer.from(url).toString("base64url")
      : btoa(url).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  const res = await get(`/admin/seo/audit/${encoded}`);
  return res?.data;
}

export async function fetchSitemapStatus() {
  const res = await get(`/admin/seo/sitemap-status`);
  return res?.data;
}

export async function fetchIntegrations() {
  const res = await get(`/admin/seo/integrations`);
  return res?.data;
}

export async function updateIntegrations(patch) {
  const res = await put(`/admin/seo/integrations`, patch);
  return res?.data;
}

export async function fetchIndexedPages() {
  const res = await get(`/admin/seo/indexed-pages`);
  return res?.data;
}

export async function updateIndexedPages(urls) {
  const res = await put(`/admin/seo/indexed-pages`, { urls });
  return res?.data;
}

/** Fetch enabled analytics providers (public endpoint, used by AnalyticsLoader) */
export async function fetchPublicAnalyticsConfig() {
  const res = await get(`/public/analytics-config`);
  return res?.data || {};
}

// ─── Phase 1: per-page meta overrides ────────────────────────────────────
export async function listPageMeta() {
  const res = await get(`/admin/seo/page-meta`);
  return res?.data || [];
}
export async function getPageMetaOne(path, lang = "en") {
  const q = new URLSearchParams({ path, lang });
  const res = await get(`/admin/seo/page-meta/one?${q.toString()}`);
  return res?.data || null;
}
export async function upsertPageMeta(payload) {
  const res = await put(`/admin/seo/page-meta`, payload);
  return res?.data;
}
export async function deletePageMeta(path, lang = "en") {
  const q = new URLSearchParams({ path, lang });
  const res = await del(`/admin/seo/page-meta?${q.toString()}`);
  return res?.data;
}
export async function fetchPageInsights(path, lang = "en") {
  const q = new URLSearchParams({ path, lang });
  const res = await get(`/admin/seo/page-insights?${q.toString()}`);
  return res?.data;
}
export async function fetchPublicPageMeta(path, lang = "en") {
  const q = new URLSearchParams({ path, lang });
  const res = await get(`/public/seo/page-meta?${q.toString()}`);
  return res?.data || null;
}

// ─── Phase 2: GSC + PageSpeed ────────────────────────────────────────────
export async function fetchGscStatus() {
  const res = await get(`/admin/seo/gsc/status`);
  return res?.data;
}
export async function saveGscCredentials(payload) {
  const res = await put(`/admin/seo/gsc/credentials`, payload);
  return res?.data;
}
export async function triggerGscSync(days = 3) {
  const res = await post(`/admin/seo/gsc/sync`, { days });
  return res?.data;
}
export async function fetchGscTopQueries({ path, limit = 50 } = {}) {
  const q = new URLSearchParams();
  if (path) q.set("path", path);
  q.set("limit", String(limit));
  const res = await get(`/admin/seo/gsc/top-queries?${q.toString()}`);
  return res?.data || [];
}
export async function runPageSpeed(url, strategy = "mobile") {
  const res = await post(`/admin/seo/pagespeed/run`, { url, strategy });
  return res?.data;
}
export async function fetchPageSpeedLatest(url, strategy = "mobile") {
  const q = new URLSearchParams({ url, strategy });
  const res = await get(`/admin/seo/pagespeed/latest?${q.toString()}`);
  return res?.data;
}
export async function savePageSpeedKey(key) {
  const res = await put(`/admin/seo/pagespeed/key`, { key });
  return res?.data;
}

// ─── Phase 3: audit history + link crawler ──────────────────────────────
export async function runAuditPersist() {
  const res = await post(`/admin/seo/audit/run`, {});
  return res?.data;
}
export async function listAuditRuns(limit = 30) {
  const res = await get(`/admin/seo/audit-runs?limit=${limit}`);
  return res?.data || [];
}
export async function fetchAuditRun(id) {
  const res = await get(`/admin/seo/audit-runs/${id}`);
  return res?.data;
}
export async function fetchAuditHistory(path) {
  const q = new URLSearchParams({ path });
  const res = await get(`/admin/seo/audit-history?${q.toString()}`);
  return res?.data || [];
}
export async function fetchAuditTrends(days = 30) {
  const res = await get(`/admin/seo/audit-trends?days=${days}`);
  return res?.data || [];
}
export async function runLinkCrawl() {
  const res = await post(`/admin/seo/links/crawl`, {});
  return res?.data;
}
export async function fetchLinkIssues({ type, includeResolved = false } = {}) {
  const q = new URLSearchParams();
  if (type) q.set("type", type);
  if (includeResolved) q.set("includeResolved", "true");
  const res = await get(`/admin/seo/links/issues?${q.toString()}`);
  return res?.data;
}
export async function resolveLinkIssue(id) {
  const res = await post(`/admin/seo/links/issues/${id}/resolve`, {});
  return res?.data;
}

// ─── Phase 4: business SEO ──────────────────────────────────────────────
export async function fetchBusinessSeoTop({ order = "desc", limit = 50 } = {}) {
  const q = new URLSearchParams({ order, limit: String(limit) });
  const res = await get(`/admin/seo/businesses/top?${q.toString()}`);
  return res?.data || [];
}
export async function fetchBusinessSeo(businessId) {
  const res = await get(`/admin/seo/businesses/${businessId}`);
  return res?.data;
}
export async function upsertBusinessSeo(businessId, payload) {
  const res = await put(`/admin/seo/businesses/${businessId}`, payload);
  return res?.data;
}
export async function scoreBusinessSeo(businessId, lang = "en") {
  const res = await post(`/admin/seo/businesses/${businessId}/score`, { lang });
  return res?.data;
}
export async function fetchPublicBusinessSeo(businessId, lang = "en") {
  const q = new URLSearchParams({ lang });
  const res = await get(`/public/seo/business/${businessId}?${q.toString()}`);
  return res?.data || null;
}
